import qs from 'qs';
import applyCaseMiddleware from 'axios-case-converter';
import axios, {
  InternalAxiosRequestConfig,
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type AxiosResponse,
} from 'axios';
import { t } from 'i18next';

import { getApiBaseUrl } from '@/utils/api/getApiBaseUrl';
import { type EnvConfig } from '@/utils/environment';
import { sentryLogIssue } from '@/utils/sentry';
import { notification } from '@/utils/notification';
import type { ApiDataResponse, ApiIncludedRecord } from '@/types/shared';

type AxiosDataResponse<TData, TIncluded extends ApiIncludedRecord | null = null, TMeta = unknown> = AxiosResponse<ApiDataResponse<TData, TIncluded, TMeta>>; // eslint-disable-line @stylistic/max-len
type AxiosMethodReturnType<TData, TIncluded extends ApiIncludedRecord | null = null, TMeta = unknown> = Promise<ApiDataResponse<TData, TIncluded, TMeta>>; // eslint-disable-line @stylistic/max-len

const queryParamsFilter = (_name: string, value: unknown) => {
  if (!value) {
    return;
  }

  return value;
};

const instance = axios.create({
  // Send the Envoy session cookie on same-origin /api/* calls; the edge mints the
  // bearer from it (no client-side token needed).
  withCredentials: true,
  headers: {
    accept: 'application/vnd.api+json',
    'content-type': 'application/vnd.api+json',
  },
  paramsSerializer: (params) => qs.stringify(params, {
    filter: queryParamsFilter,
    indices: false,
  }),
});

instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (!config.baseURL) {
    /**
     * NOTE:
     * this could happen when you try to call Api request before Config service
     * is initialized
     */
    throw new Error('Api not initialized!');
  }

  if (!config.headers) {
    config.headers = {} as AxiosRequestHeaders;
  }

  // Blob downloads either stream from our API (same-origin) or reach a GCS signed
  // URL — directly (an absolute storage.googleapis.com URL) or via a 302 from our
  // API. The instance default withCredentials: true turns that cross-origin GCS
  // request into a credentialed one, which GCS blocks because it cannot return
  // Access-Control-Allow-Credentials: true. Credentials only matter cross-origin,
  // and same-origin blob calls still send the session cookie regardless of this
  // flag, so dropping it is safe for every blob download and fixes the GCS ones.
  if (config.responseType === 'blob') {
    config.withCredentials = false;
  }

  return config;
});

let isNotificationFor401Shown = false;

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const responseStatus = error.response?.status;

    if (responseStatus === 401) {
      if (window.location.pathname.includes('app/veriforms')) {
        return Promise.reject(error);
      }

      sentryLogIssue(error);

      if (!isNotificationFor401Shown) {
        notification.danger({ body: t('common:errors.tryAgain') });
        isNotificationFor401Shown = true;
        window.location.assign('/auth/signin');

        setTimeout(() => {
          isNotificationFor401Shown = false;
        }, 2000);
      }
    }

    return Promise.reject(error);
  },
);

const caseConverterOptions = {
  preservedKeys: (input: string) => {
    return /\./.test(input);
  },
};

applyCaseMiddleware(instance, caseConverterOptions);

const performRequest = async <
  TData,
  TIncluded extends ApiIncludedRecord | null = null,
  TMeta = unknown,
>(
  request: Promise<AxiosDataResponse<TData, TIncluded, TMeta>>,
): Promise<ApiDataResponse<TData, TIncluded, TMeta>> => {
  const response = await request;

  return response.data;
};

const axiosGet = <
  TData,
  TIncluded extends ApiIncludedRecord | null = null,
  TMeta = unknown,
>(
  url: string,
  config?: AxiosRequestConfig<unknown>,
): AxiosMethodReturnType<TData, TIncluded, TMeta> => {

  const request = instance.get<ApiDataResponse<TData, TIncluded, TMeta>>(url, config);

  return performRequest<TData, TIncluded, TMeta>(request);
};

const axiosPost = <TData>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig<unknown>,
): AxiosMethodReturnType<TData> => {
  return performRequest(instance.post(url, data, config));
};

const axiosPatch = <
  TData,
  TIncluded extends ApiIncludedRecord | null = null,
  TMeta = unknown,
>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig<unknown>,
): AxiosMethodReturnType<TData, TIncluded, TMeta> => {

  return performRequest(instance.patch(url, data, config));
};

const axiosDelete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return instance.delete<AxiosResponse>(url, config);
};

const axiosPut = <TData>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig<unknown>,
): AxiosMethodReturnType<TData> => {
  return performRequest(instance.put(url, data, config));
};

const axiosPostForm = <TData>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig<unknown>,
): AxiosMethodReturnType<TData> => {
  return performRequest(instance.postForm(url, data, config));
};

const axiosInitialize = (config: EnvConfig) => {
  instance.defaults.baseURL = getApiBaseUrl(config);
};

export const Api = {
  initialize: axiosInitialize,
  get: axiosGet,
  post: axiosPost,
  patch: axiosPatch,
  delete: axiosDelete,
  put: axiosPut,
  postForm: axiosPostForm,
};
